/**
 * OptimizerModal — Parlay optimizer modal (Elite only)
 *
 * Self-contained workflow:
 * 1. Shows selected legs with ML scores
 * 2. Strategy config (collapsible) — mirrors AI Config panel settings
 * 3. Optimize button
 * 4. Results: optimized parlays with Track/Track All buttons
 *
 * Two entry points: Builder (4+ legs) or Picks view (post-generation)
 */

import type {
  AnalysisConfig,
  LegScoringStrategy,
  ParlayIntelligenceConfig,
} from '@parlay-picker/shared/analysisConfig';
import type { OptimizedParlaySet } from '@parlay-picker/shared/api/mlApi';
import type { LeagueKey } from '@parlay-picker/shared/league';
import type { LegProvenance } from '@parlay-picker/shared/legProvenance';
import {
  getMlRecommendationDisplay,
  type MlRecommendationTone,
} from '@parlay-picker/shared/mlRecommendation';
import { annotateStrengthPercentiles } from '@parlay-picker/shared/parlayStrength';
import { formatStatLabel } from '@parlay-picker/shared/statLabels';
import { ChevronDown, ChevronUp, Sparkles, Target, X, Zap } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { BoostControl, ConfigControl, FilterToggle } from './OptimizerModalControls';

/** Grey for a head that made no call, same as the tertiary text it already used. */
const OPTIMIZER_TONE_CLASS: Record<MlRecommendationTone, string> = {
  positive: 'text-emerald-400',
  lean: 'text-blue-400',
  neutral: 'text-theme-text-tertiary',
  negative: 'text-red-400',
  none: 'text-theme-text-tertiary',
};

// =============================================================================
// Types
// =============================================================================

export interface OptimizerLeg {
  id: string;
  player: string;
  team: string;
  statType: string;
  line: number | null;
  overUnder: string | null;
  odds: string;
  gameId: string;
  score: number;
  sportsbook?: string;
  leagueKey: LeagueKey;
  opponent?: string | null;
  /** True when `team` plays at home */
  isHomeGame?: boolean | null;
  /** LLM analysis preserved from generation */
  aiEdge?: string | null;
  aiConfidence?: string | null;
  aiReasoning?: string | null;
  /** ML score from ml_prop_scores */
  mlScore?: number | null;
  mlRecommendation?: string | null;
  /** Carried to the track request so each leg keeps what chose it */
  provenance?: LegProvenance;
  predictionId?: string;
  factorsUsed?: AnalysisConfig | null;
}

interface OptimizerModalProps {
  isOpen: boolean;
  onClose: () => void;
  legs: OptimizerLeg[];
  loading: boolean;
  error: string | null;
  result: OptimizedParlaySet | null;
  onOptimize: (config?: Partial<ParlayIntelligenceConfig>) => void;
  onClearResult: () => void;
  onTrackParlay?: (parlayIndex: number) => void;
  onTrackAll?: () => void;
  /** User's saved config defaults from analysisConfig.parlayIntelligence */
  defaultConfig?: Partial<ParlayIntelligenceConfig>;
  /** Callback to enrich parlays with Opus narrative */
  onEnrichNarrative?: () => void;
  /** Whether narrative enrichment is loading */
  enrichingNarrative?: boolean;
  /** Enriched narrative texts (one per parlay) */
  narratives?: string[];
}

const STRATEGY_OPTIONS: Array<{
  value: LegScoringStrategy;
  label: string;
  winRate: string;
  formula: string;
}> = [
  {
    value: 'ml_light_trend',
    label: 'Trend + ML',
    winRate: 'Q1: 68%',
    formula: 'ML×0.3 + Trend×10 + Boosts×5',
  },
  {
    value: 'trend_boosted',
    label: 'Trend + Boosts',
    winRate: 'Q1: 64%',
    formula: 'Trend×10 + Edge×3 + Boosts×5',
  },
  {
    value: 'opp_boosted_ml',
    label: 'Team-Boosted ML',
    winRate: 'Legacy',
    formula: 'ML + (OppRate − 50)×0.5',
  },
  {
    value: 'ml_score',
    label: 'ML Score Only',
    winRate: 'Baseline',
    formula: 'ML score only',
  },
];

// =============================================================================
// Component
// =============================================================================

export function OptimizerModal({
  isOpen,
  onClose,
  legs,
  loading,
  error,
  result,
  onOptimize,
  onClearResult,
  onTrackParlay,
  onTrackAll,
  defaultConfig,
  onEnrichNarrative,
  enrichingNarrative,
  narratives,
}: OptimizerModalProps) {
  const [showConfig, setShowConfig] = useState(false);

  // Strategy
  const [strategy, setStrategy] = useState<LegScoringStrategy>(
    defaultConfig?.legScoringStrategy ?? 'ml_light_trend',
  );

  // Constructor
  const [maxAnchorLegs, setMaxAnchorLegs] = useState(defaultConfig?.maxAnchorLegs ?? 2);
  const [maxLegReuse, setMaxLegReuse] = useState(defaultConfig?.maxLegReuse ?? 2);
  const [threeLegParlays, setThreeLegParlays] = useState(defaultConfig?.threeLegParlays ?? 2);
  const [maxLegsToConsider, setMaxLegsToConsider] = useState(defaultConfig?.maxLegsToConsider ?? 6);
  const [includeLottery, setIncludeLottery] = useState(
    defaultConfig?.includeLotteryParlay ?? false,
  );
  const [useBruteForce, setUseBruteForce] = useState(defaultConfig?.useBruteForce ?? false);

  // Hard filters
  const [rejectUnderAway, setRejectUnderAway] = useState(defaultConfig?.rejectUnderAway !== false);
  const [rejectB2bOver, setRejectB2bOver] = useState(defaultConfig?.rejectB2bOver !== false);
  const [rejectColdWeakEdge, setRejectColdWeakEdge] = useState(
    defaultConfig?.rejectColdWeakEdge !== false,
  );
  const [rejectUnderModFav, setRejectUnderModFav] = useState(
    defaultConfig?.rejectUnderModFavorite?.enabled !== false,
  );

  // Score boosts
  const [overModFavBoost, setOverModFavBoost] = useState(defaultConfig?.overModFavoriteBoost ?? 3);
  const [overAssistsBoost, setOverAssistsBoost] = useState(defaultConfig?.overAssistsBoost ?? 2);
  const [overReboundsBoost, setOverReboundsBoost] = useState(defaultConfig?.overReboundsBoost ?? 1);
  const [hotTrendBoost, setHotTrendBoost] = useState(defaultConfig?.hotTrendBoost ?? 2);

  const handleOptimize = useCallback(() => {
    onOptimize({
      legScoringStrategy: strategy,
      maxAnchorLegs,
      maxLegReuse,
      threeLegParlays,
      maxLegsToConsider,
      includeLotteryParlay: includeLottery,
      useBruteForce,
      rejectUnderAway,
      rejectB2bOver,
      rejectColdWeakEdge,
      rejectUnderModFavorite: {
        enabled: rejectUnderModFav,
        spreadMin: -6.5,
        spreadMax: -1,
      },
      overModFavoriteBoost: overModFavBoost,
      overAssistsBoost,
      overReboundsBoost,
      hotTrendBoost,
    });
  }, [
    onOptimize,
    strategy,
    maxAnchorLegs,
    maxLegReuse,
    threeLegParlays,
    maxLegsToConsider,
    includeLottery,
    useBruteForce,
    rejectUnderAway,
    rejectB2bOver,
    rejectColdWeakEdge,
    rejectUnderModFav,
    overModFavBoost,
    overAssistsBoost,
    overReboundsBoost,
    hotTrendBoost,
  ]);

  // Annotate result parlays with slate-relative strength percentiles so the
  // UI can show a "Top X%" badge alongside the odds-based tier. This is a
  // presentation-layer concern — the constructor stays focused on building
  // valid combinations, this layer labels them for the user.
  const annotatedParlays = useMemo(
    () => (result ? annotateStrengthPercentiles(result.parlays) : []),
    [result],
  );

  if (!isOpen) return null;

  const canOptimize = legs.length >= 4;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-xl max-h-[85vh] overflow-y-auto bg-theme-surface border border-theme-border rounded-2xl shadow-2xl mx-4">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-theme-surface border-b border-theme-border px-5 py-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-purple-600/20 flex items-center justify-center">
              <Zap className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h2 className="font-semibold text-theme-text">Parlay Optimizer</h2>
              <p className="text-xs text-theme-text-tertiary">{legs.length} legs loaded</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-theme-text-tertiary hover:text-theme-text p-1 rounded-lg hover:bg-theme-surface-hover"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Legs list */}
          <div>
            <h3 className="text-xs font-medium text-theme-text-tertiary uppercase tracking-wide mb-2">
              Selected Legs
            </h3>
            <div className="space-y-1.5">
              {legs.map((leg) => (
                <div
                  key={leg.id}
                  className="px-3 py-2.5 bg-theme-surface rounded-lg border border-theme-border/50"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-theme-text">{leg.player}</span>
                      <span className="text-xs px-1.5 py-0.5 rounded bg-theme-surface-hover text-theme-text-secondary">
                        {leg.overUnder} {leg.line} {formatStatLabel(leg.statType)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {leg.mlScore != null && (
                        <span
                          className={`text-xs font-semibold px-1.5 py-0.5 rounded ${
                            leg.mlScore >= 70
                              ? 'bg-emerald-500/15 text-emerald-400'
                              : leg.mlScore >= 50
                                ? 'bg-yellow-500/15 text-yellow-400'
                                : 'bg-red-500/15 text-red-400'
                          }`}
                        >
                          ML {leg.mlScore}
                        </span>
                      )}
                      {leg.mlRecommendation &&
                        (() => {
                          const rec = getMlRecommendationDisplay(leg.mlRecommendation);
                          return (
                            <span
                              title={rec.description}
                              className={`text-xs font-medium ${OPTIMIZER_TONE_CLASS[rec.tone]}`}
                            >
                              {rec.label}
                            </span>
                          );
                        })()}
                      <span className="text-xs font-mono text-theme-text-secondary">
                        {leg.odds}
                      </span>
                    </div>
                  </div>
                  {(leg.opponent || leg.aiEdge) && (
                    <div className="flex items-center gap-3 mt-1">
                      {leg.opponent && (
                        <span className="text-xs text-theme-text-tertiary">vs {leg.opponent}</span>
                      )}
                      {leg.aiEdge && (
                        <span
                          className={`text-xs ${
                            leg.aiEdge.toLowerCase().includes('strong')
                              ? 'text-emerald-400'
                              : leg.aiEdge.toLowerCase().includes('moderate')
                                ? 'text-yellow-400'
                                : 'text-theme-text-tertiary'
                          }`}
                        >
                          {leg.aiEdge} edge
                        </span>
                      )}
                    </div>
                  )}
                  {leg.aiReasoning && (
                    <p className="text-xs text-theme-text-tertiary mt-1 line-clamp-2">
                      {leg.aiReasoning}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Strategy config */}
          <div className="border border-theme-border rounded-xl overflow-hidden">
            <button
              type="button"
              onClick={() => setShowConfig(!showConfig)}
              className="w-full flex items-center justify-between px-4 py-3 bg-theme-surface hover:bg-theme-surface-hover transition-colors"
            >
              <span className="text-sm font-medium text-theme-text">Strategy Settings</span>
              {showConfig ? (
                <ChevronUp className="w-4 h-4 text-theme-text-tertiary" />
              ) : (
                <ChevronDown className="w-4 h-4 text-theme-text-tertiary" />
              )}
            </button>

            {showConfig && (
              <div className="px-4 pb-4 pt-2 bg-theme-surface space-y-4">
                {/* Scoring Strategy */}
                <div>
                  <label className="text-xs font-medium text-theme-text-tertiary uppercase tracking-wide block mb-2">
                    Leg Scoring Strategy
                  </label>
                  <div className="space-y-1">
                    {STRATEGY_OPTIONS.map((opt) => {
                      const isActive = strategy === opt.value;
                      return (
                        <button
                          type="button"
                          key={opt.value}
                          onClick={() => setStrategy(opt.value)}
                          className={`w-full text-left px-3 py-2 rounded-lg text-sm transition ${
                            isActive
                              ? 'bg-purple-600/20 border border-purple-500/40 text-purple-300'
                              : 'bg-theme-surface hover:bg-theme-surface-hover text-theme-text-secondary border border-transparent'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span>{opt.label}</span>
                            <span
                              className={`text-xs ${isActive ? 'text-emerald-400' : 'text-theme-text-tertiary'}`}
                            >
                              {opt.winRate}
                            </span>
                          </div>
                          <div className="text-xs text-theme-text-tertiary/60 mt-0.5 font-mono">
                            {opt.formula}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Hard Filters */}
                <div className="border-t border-theme-border pt-3">
                  <label className="text-xs font-medium text-theme-text-tertiary uppercase tracking-wide block mb-2">
                    Strategy Filters
                  </label>
                  <div className="space-y-2">
                    <FilterToggle
                      label="Reject Under + Mod Favorite"
                      description="Under on teams favored by 1-6.5 pts hit 17%"
                      checked={rejectUnderModFav}
                      onChange={setRejectUnderModFav}
                    />
                    <FilterToggle
                      label="Reject Under + Away"
                      description="Under on away players hit 23%"
                      checked={rejectUnderAway}
                      onChange={setRejectUnderAway}
                    />
                    <FilterToggle
                      label="Reject B2B + Over"
                      description="Over on back-to-back players hit 0%"
                      checked={rejectB2bOver}
                      onChange={setRejectB2bOver}
                    />
                    <FilterToggle
                      label="Reject Cold + Weak Edge"
                      description="Cold trend without strong edge hit 14%"
                      checked={rejectColdWeakEdge}
                      onChange={setRejectColdWeakEdge}
                    />
                  </div>
                </div>

                {/* Score Boosts */}
                <div className="border-t border-theme-border pt-3">
                  <label className="text-xs font-medium text-theme-text-tertiary uppercase tracking-wide block mb-2">
                    Score Boosts
                  </label>
                  <div className="space-y-2">
                    <BoostControl
                      label="Over + Mod Favorite"
                      value={overModFavBoost}
                      max={6}
                      onChange={setOverModFavBoost}
                    />
                    <BoostControl
                      label="Over Assists"
                      value={overAssistsBoost}
                      max={5}
                      onChange={setOverAssistsBoost}
                    />
                    <BoostControl
                      label="Over Rebounds"
                      value={overReboundsBoost}
                      max={5}
                      onChange={setOverReboundsBoost}
                    />
                    <BoostControl
                      label="Hot Trend"
                      value={hotTrendBoost}
                      max={5}
                      onChange={setHotTrendBoost}
                    />
                  </div>
                </div>

                {/* Constructor Params */}
                <div className="border-t border-theme-border pt-3">
                  <label className="text-xs font-medium text-theme-text-tertiary uppercase tracking-wide block mb-2">
                    Constructor
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <ConfigControl
                      label="Max Legs"
                      value={maxLegsToConsider}
                      options={[4, 5, 6, 7, 8]}
                      onChange={setMaxLegsToConsider}
                    />
                    <ConfigControl
                      label="Anchors"
                      value={maxAnchorLegs}
                      options={[1, 2, 3]}
                      onChange={setMaxAnchorLegs}
                    />
                    <ConfigControl
                      label="Max Reuse"
                      value={maxLegReuse}
                      options={[1, 2, 3, 4, 5]}
                      onChange={setMaxLegReuse}
                    />
                    <ConfigControl
                      label="3-Leg Parlays"
                      value={threeLegParlays}
                      options={[0, 1, 2, 3, 4]}
                      onChange={setThreeLegParlays}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-sm text-theme-text-secondary">
                      Include Lottery (4-6 legs)
                    </span>
                    <button
                      type="button"
                      onClick={() => setIncludeLottery(!includeLottery)}
                      className={`w-10 h-5 rounded-full transition-colors relative ${
                        includeLottery ? 'bg-purple-500' : 'bg-theme-border'
                      }`}
                    >
                      <div
                        className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                          includeLottery ? 'left-5' : 'left-0.5'
                        }`}
                      />
                    </button>
                  </div>

                  <div className="mt-3 p-3 rounded-lg border border-theme-border/60 bg-theme-surface/40">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-theme-text">
                            Exhaustive Search
                          </span>
                          <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300">
                            Beta
                          </span>
                        </div>
                        <p className="text-xs text-theme-text-tertiary mt-1 leading-relaxed">
                          Enumerates every valid 2-leg and 3-leg combination instead of the default
                          greedy walk. Lets your top leg anchor multiple 2-leg parlays — a pattern
                          the greedy optimizer structurally can't produce. Best for slates with a
                          clear standout pick. Slightly slower; capped at 10 legs.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setUseBruteForce(!useBruteForce)}
                        aria-label="Toggle exhaustive search"
                        className={`flex-shrink-0 mt-0.5 w-10 h-5 rounded-full transition-colors relative ${
                          useBruteForce ? 'bg-purple-500' : 'bg-theme-border'
                        }`}
                      >
                        <div
                          className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                            useBruteForce ? 'left-5' : 'left-0.5'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="text-sm text-red-400 bg-red-400/10 border border-red-500/20 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          {/* Optimize button */}
          {!result && (
            <button
              type="button"
              onClick={handleOptimize}
              disabled={!canOptimize || loading}
              className={`w-full py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all ${
                canOptimize && !loading
                  ? 'bg-purple-600 hover:bg-purple-500 text-white shadow-lg shadow-purple-600/20'
                  : 'bg-theme-surface-hover text-theme-text-tertiary cursor-not-allowed'
              }`}
            >
              {loading ? (
                <>
                  <Sparkles className="w-4 h-4 animate-spin" />
                  Optimizing parlays...
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4" />
                  {canOptimize
                    ? `Optimize ${legs.length} Legs`
                    : `Need ${4 - legs.length} more legs`}
                </>
              )}
            </button>
          )}

          {/* Results */}
          {result && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-emerald-400" />
                  <span className="text-sm font-medium text-theme-text">
                    {result.parlays.length} Optimized Parlays
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={onClearResult}
                    className="text-xs text-theme-text-tertiary hover:text-theme-text px-2 py-1 rounded-lg hover:bg-theme-surface-hover"
                  >
                    Re-optimize
                  </button>
                  {onTrackAll && (
                    <button
                      type="button"
                      onClick={onTrackAll}
                      className="text-xs bg-purple-600 text-white px-3 py-1.5 rounded-lg hover:bg-purple-500"
                    >
                      Track All
                    </button>
                  )}
                </div>
              </div>

              {/* Enrich with AI button */}
              {onEnrichNarrative && !narratives?.length && (
                <button
                  type="button"
                  onClick={onEnrichNarrative}
                  disabled={enrichingNarrative}
                  className="w-full py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 bg-purple-500/15 text-purple-400 border border-purple-500/20 hover:bg-purple-500/25 disabled:opacity-50"
                >
                  {enrichingNarrative ? (
                    <>
                      <Sparkles className="w-4 h-4 animate-spin" /> Generating AI analysis...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" /> Enrich with Opus AI
                    </>
                  )}
                </button>
              )}

              <div className="text-xs text-theme-text-tertiary">
                {result.metadata.slateType} slate · {result.metadata.uniqueGames} games ·{' '}
                {result.anchors.length} anchors
              </div>

              {annotatedParlays.map((parlay, i) => (
                <div
                  key={i}
                  className="bg-theme-surface rounded-xl border border-theme-border overflow-hidden"
                >
                  {/* Parlay header */}
                  <div className="flex items-center justify-between px-4 py-3 bg-theme-surface-hover/50">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-semibold text-purple-400">{parlay.tier}</span>
                      <span className="text-xs font-mono text-emerald-400">
                        {parlay.combinedOdds}
                      </span>
                      {parlay.isAnchorParlay && (
                        <span className="text-[10px] bg-amber-600/80 text-white px-1.5 py-0.5 rounded font-medium">
                          Anchor
                        </span>
                      )}
                      <span className="text-[10px] bg-purple-600/80 text-white px-1.5 py-0.5 rounded font-medium">
                        Optimized
                      </span>
                      {typeof parlay.strengthPercentile === 'number' && (
                        <span
                          title="Quality rank within this optimized set. Computed from each parlay's mean leg score vs the rest of the set."
                          className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                            parlay.strengthPercentile >= 0.9
                              ? 'bg-emerald-500/20 text-emerald-300'
                              : parlay.strengthPercentile >= 0.7
                                ? 'bg-sky-500/20 text-sky-300'
                                : parlay.strengthPercentile >= 0.3
                                  ? 'bg-theme-surface text-theme-text-secondary'
                                  : 'bg-theme-surface text-theme-text-tertiary'
                          }`}
                        >
                          Top {Math.max(1, Math.round((1 - parlay.strengthPercentile) * 100))}%
                        </span>
                      )}
                    </div>
                    {onTrackParlay && (
                      <button
                        type="button"
                        onClick={() => onTrackParlay(i)}
                        className="text-xs bg-purple-600/15 text-purple-400 hover:bg-purple-600/25 px-3 py-1.5 rounded-lg font-medium"
                      >
                        Track
                      </button>
                    )}
                  </div>

                  {/* Narrative */}
                  <div className="px-4 pt-2">
                    {narratives?.[i] ? (
                      <p className="text-sm text-theme-text-secondary leading-relaxed mb-2 border-l-2 border-purple-500/30 pl-3">
                        {narratives[i]}
                      </p>
                    ) : (
                      <p className="text-xs text-theme-text-tertiary mb-2 italic">
                        {(parlay as unknown as Record<string, string>).reasoning ??
                          'ML-optimized parlay'}
                      </p>
                    )}
                  </div>

                  {/* Legs */}
                  <div className="px-4 pb-3 space-y-1.5">
                    {parlay.legs.map((leg, j) => {
                      const inputLeg = legs.find(
                        (l) =>
                          l.player === leg.player &&
                          l.statType === leg.statType &&
                          l.line === leg.line,
                      );
                      const isAnchor = result.anchors.some(
                        (aId) =>
                          inputLeg?.id === aId ||
                          legs.find(
                            (l) =>
                              l.id === aId &&
                              l.player === leg.player &&
                              l.statType === leg.statType,
                          ),
                      );
                      return (
                        <div
                          key={j}
                          className={`px-3 py-2 rounded-lg border ${
                            isAnchor
                              ? 'bg-amber-500/5 border-amber-500/20'
                              : 'bg-theme-surface border-theme-border/30'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-theme-text">
                                {leg.player}
                              </span>
                              <span className="text-xs px-1.5 py-0.5 rounded bg-theme-surface-hover text-theme-text-secondary">
                                {leg.overUnder} {leg.line} {formatStatLabel(leg.statType)}
                              </span>
                              {isAnchor && (
                                <span className="text-[10px] bg-amber-600/80 text-white px-1.5 py-0.5 rounded font-medium">
                                  Anchor
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5">
                              {inputLeg?.mlScore != null && (
                                <span
                                  className={`text-xs font-semibold px-1.5 py-0.5 rounded ${
                                    inputLeg.mlScore >= 70
                                      ? 'bg-emerald-500/15 text-emerald-400'
                                      : inputLeg.mlScore >= 50
                                        ? 'bg-yellow-500/15 text-yellow-400'
                                        : 'bg-red-500/15 text-red-400'
                                  }`}
                                >
                                  {inputLeg.mlScore}
                                </span>
                              )}
                              <span className="text-xs font-mono text-theme-text-tertiary">
                                {leg.odds}
                              </span>
                            </div>
                          </div>
                          {(inputLeg?.opponent || inputLeg?.aiEdge) && (
                            <div className="flex items-center gap-2 mt-0.5">
                              {inputLeg?.opponent && (
                                <span className="text-xs text-theme-text-tertiary">
                                  vs {inputLeg.opponent}
                                </span>
                              )}
                              {inputLeg?.aiEdge && (
                                <span
                                  className={`text-xs ${
                                    inputLeg.aiEdge.toLowerCase().includes('strong')
                                      ? 'text-emerald-400'
                                      : inputLeg.aiEdge.toLowerCase().includes('moderate')
                                        ? 'text-yellow-400'
                                        : 'text-theme-text-tertiary'
                                  }`}
                                >
                                  {inputLeg.aiEdge} edge
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
